/**
 * How each slot type renders itself.
 *
 * The twin of `SlotLogic`, for appearance rather than behavior. A slot's `type`
 * tag is a DISPATCH KEY here too: each type declares its rendering once — its
 * accent class, its badge glyph, its readonly preview, its editable body, and
 * whether the edit card's Insert menu can write into its `expr` — as one
 * `SlotTypeView` object, and the `SLOT_VIEWS` registry maps the tag to it.
 * `view(slot)` is the lookup, so the three shells below (`SlotBadge`,
 * `SlotPreview`, `SlotEditorBody`) are one-liners that hand the slot back to its
 * own type's implementation instead of switching on the tag.
 *
 * Adding a type therefore means adding one entry here: the registry's
 * `satisfies` clause covers every tag in the union, so the compiler names the
 * type that is missing rather than leaving a badge silently blank.
 *
 * The per-type pieces live in leaf modules — `SlotGlyphs`, `SlotPreviews`,
 * `SlotEditors` — which know nothing of this registry, so the shells can be
 * imported by the very cards those pieces are drawn inside.
 */

import React from 'react';
import type {
  FillInSlot,
  GoalSlot,
  OptionalSlot,
  SelectSlot,
  TextSlot,
} from './schema';
import type { SlotOfType, SlotType } from './slot_logic';
import type { SharingFormSlot } from './types';
import {
  FillInGlyph,
  GoalGlyph,
  OptionalGlyph,
  SelectGlyph,
  TextGlyph,
} from './SlotGlyphs';
import {
  FillInPreview,
  GoalPreview,
  OptionalPreview,
  SelectPreview,
  TextPreview,
  type SlotPreviewProps,
} from './SlotPreviews';
import {
  FillInSlotEditor,
  GoalSlotEditor,
  OptionalSlotEditor,
  SelectSlotEditor,
  TextSlotEditor,
  SharingModesRow,
  VariableRows,
  type SlotEditorProps,
} from './SlotEditors';
import { SlotLogic } from './slot_logic';
import styles from './slots.module.css';

/**
 * One slot type's rendering, over slots already narrowed to that type.
 *
 * Members are declared as METHODS on purpose, exactly as `SlotTypeLogic`'s are:
 * TypeScript checks method parameters bivariantly, which is what lets the
 * registry hand a `SlotTypeView<SelectSlot>` back through `view()` as a
 * `SlotTypeView<SharingFormSlot>`. Declaring `Editor` as a
 * `React.FC<SlotEditorProps<S>>` property instead would be checked
 * contravariantly and would not compile. Each implementation still only ever
 * sees its own slot, because the shells below pass back the very slot they
 * looked the view up with.
 */
export interface SlotTypeView<S extends SharingFormSlot = SharingFormSlot> {
  readonly type: S['type'];
  /**
   * The accent class a card of this type carries. A type class sets only custom
   * properties (`--slot-accent`, `--slot-tint`, `--slot-dash`), so everything
   * inside the card reads the accent by inheritance rather than being handed
   * the type itself — put this on the card, not on each child.
   */
  readonly className: string;
  /** The mark inside the badge; the 28px badge around it is shared. */
  Glyph(): React.ReactElement | null;
  /** The readonly display a collapsed card shows. */
  Preview(props: SlotPreviewProps<S>): React.ReactElement | null;
  /** The editable display inside the expanded edit card. */
  Editor(props: SlotEditorProps<S>): React.ReactElement | null;
  /**
   * The `expr` the edit card's Insert menu writes into, or null when this type
   * offers no Insert menu. A type that answers with a string gets the menu.
   */
  insertableText(slot: S): string | null;
  /** The same slot carrying a new `expr` (a no-op for the types that take none). */
  withInsertedText(slot: S, text: string): S;
}

/** A slot nothing can be inserted into — the default. */
const defaultSlotView = {
  insertableText: () => null,
  withInsertedText: keepText,
} as const;

/** Inserting into a slot that takes no insertions leaves it as it was. */
function keepText<S extends SharingFormSlot>(slot: S): S {
  return slot;
}

/**
 * The types whose editor is a plain textarea the Insert menu can write into. An
 * `optional` slot has an `expr` too, but edits through a single-line field and
 * offers no menu, so it keeps the default.
 */
const insertableExprView = {
  insertableText: (slot: TextSlot | GoalSlot) => slot.expr,
  withInsertedText: <S extends TextSlot | GoalSlot>(
    slot: S,
    text: string
  ): S => ({
    ...slot,
    expr: text,
  }),
} as const;

const TEXT: SlotTypeView<TextSlot> = {
  ...insertableExprView,
  type: 'text',
  className: styles.typeText,
  Glyph: TextGlyph,
  Preview: TextPreview,
  Editor: TextSlotEditor,
};

const GOAL: SlotTypeView<GoalSlot> = {
  ...insertableExprView,
  type: 'goal',
  className: styles.typeGoal,
  Glyph: GoalGlyph,
  Preview: GoalPreview,
  Editor: GoalSlotEditor,
};

const OPTIONAL: SlotTypeView<OptionalSlot> = {
  ...defaultSlotView,
  type: 'optional',
  className: styles.typeOptional,
  Glyph: OptionalGlyph,
  Preview: OptionalPreview,
  Editor: OptionalSlotEditor,
};

const SELECT: SlotTypeView<SelectSlot> = {
  ...defaultSlotView,
  type: 'select',
  className: styles.typeSelect,
  Glyph: SelectGlyph,
  Preview: SelectPreview,
  Editor: SelectSlotEditor,
};

const FILL_IN: SlotTypeView<FillInSlot> = {
  ...defaultSlotView,
  type: 'fillIn',
  className: styles.typeFillIn,
  Glyph: FillInGlyph,
  Preview: FillInPreview,
  Editor: FillInSlotEditor,
};

/**
 * Every slot type's rendering, by tag. `satisfies` is what makes this
 * exhaustive: a new member of the slot union with no entry here is a compile
 * error naming exactly the type that is missing.
 */
export const SLOT_VIEWS = {
  text: TEXT,
  goal: GOAL,
  optional: OPTIONAL,
  select: SELECT,
  fillIn: FILL_IN,
} as const satisfies { readonly [T in SlotType]: SlotTypeView<SlotOfType<T>> };

/**
 * The rendering for a slot, or for a type named on its own (the badge knows
 * only the type it is drawing).
 *
 * The lookup hands back the registry entry itself rather than one bound to the
 * slot, so a caller passes the slot again — that is what the one-liners below
 * are for, and it is deliberate. A bound entry would have to be built per call,
 * which would give `Editor` a new function identity on every render: React
 * reconciles by element type, so the open card would remount each keystroke and
 * drop the caret `SlotEditCard` writes into. The registry entry is one object
 * for the life of the module, so nothing remounts.
 */
function view(slotOrType: SharingFormSlot | SlotType): SlotTypeView {
  return SLOT_VIEWS[
    typeof slotOrType === 'string' ? slotOrType : slotOrType.type
  ];
}

/** The accent class for a slot type — put it on the card, not on each child. */
export const typeClass = (type: SlotType): string => view(type).className;

/** The `expr` the Insert menu writes into, or null when the type offers none. */
export function insertableTextOf(slot: SharingFormSlot): string | null {
  return view(slot).insertableText(slot);
}

/** The same slot carrying a new `expr` (a no-op for the types that take none). */
export function withInsertedText(
  slot: SharingFormSlot,
  text: string
): SharingFormSlot {
  return view(slot).withInsertedText(slot, text);
}

/**
 * The 28px badge that opens every slot card. The shell is one for all types;
 * only the glyph inside it differs, and the color comes by inheritance from the
 * card's accent class.
 */
export const SlotBadge: React.FC<{ type: SlotType }> = ({ type }) => {
  const { Glyph } = view(type);
  return (
    <span className={styles.badge} aria-hidden="true">
      <Glyph />
    </span>
  );
};

/**
 * The readonly display for one slot, drawn by its own type.
 *
 * The variable options are the same for every type that takes them, so the
 * shell draws them as chips under the preview: one saying the slot is excluded
 * from the message, and one naming the variable it assigns, so the author can
 * tell without opening the card what it prints and what it sets. A third marks
 * the slot the Settings tab takes the allowed detail from.
 */
export const SlotPreview: React.FC<
  SlotPreviewProps<SharingFormSlot>
> = props => {
  const { Preview } = view(props.slot);
  const variable = SlotLogic.declaredVariableName(props.slot);
  const emits = SlotLogic.emitsInMessage(props.slot);
  const allowedDetail =
    props.context.allowedDetailSlotId != null &&
    props.slot.id === props.context.allowedDetailSlotId;
  if (variable === null && emits && !allowedDetail)
    return <Preview {...props} />;
  return (
    <>
      <Preview {...props} />
      <span className={styles.variableChips}>
        {!emits && (
          <span className={styles.excludedTag} data-testid="slot-not-printed">
            Excluded from msg
          </span>
        )}
        {variable !== null && (
          <span className={styles.variableTag} data-testid="slot-variable-tag">
            → {variable === '' ? '?' : variable}
          </span>
        )}
        {allowedDetail && (
          <span
            className={styles.allowedDetailTag}
            data-testid="slot-allowed-detail"
          >
            Allowed detail
          </span>
        )}
      </span>
    </>
  );
};

/**
 * The body editor for one slot, drawn by its own type. Each editor takes the
 * slot already narrowed to its own type, so none of them re-checks what it was
 * handed.
 *
 * The sharing-mode setting belongs to every slot, and the variable options to
 * every type that takes them, so they are drawn here once rather than repeated
 * in each editor — below the type's own body, where the per-type settings end.
 */
export const SlotEditorBody: React.FC<SlotEditorProps> = props => {
  const { Editor } = view(props.slot);
  return (
    <>
      <Editor {...props} />
      <SharingModesRow
        slot={props.slot}
        testId={props.testId}
        onChange={props.onChange}
      />
      {SlotLogic.takesVariable(props.slot) && (
        <VariableRows
          slot={props.slot}
          testId={props.testId}
          context={props.context}
          onChange={props.onChange}
        />
      )}
    </>
  );
};
